import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { setAuthState } from '../composables/auth.js';
import { trackQueueEvent } from '../composables/autoapply-queue-analytics.js';
import { isAutoApplyQueueEnabled } from '../composables/autoapply-queue.js';
import { isMobileViewport } from '../composables/isMobile.js';
import { persistTutorialOption } from '../composables/tutorial-flag.js';
import { useTutorialTour } from '../composables/use-tutorial-tour.js';
import { AUTOAPPLY_QUEUE_EVENTS } from '../constants/analytics-events.js';
import { REVIEWABLE_STATUSES, isResumeMismatch, reviewOrder } from '../utils/autoapply-queue.js';
// The queue and AutoApply stores import each other, and the queue store imports this one back, so this store calls
// them only inside its computeds, watchers and actions, never at module-eval time.
// eslint-disable-next-line import/no-cycle
import { useAutoApplyJobsStore } from './auto-apply.store.js';
import { useAutoApplyQueueStore } from './autoapply-queue.store.js';
import { useOnboardingRetentionStore } from './onboarding-retention.store';

// users.options keys of the AutoApply Queue tutorial for new users (EN-1251): whether it is done with, and the
// application it follows from the Job Board to its review.
export const NEW_USER_TUTORIAL_PASSED = 'auto_apply_queue_new_user_tutorial_passed';
export const NEW_USER_TUTORIAL_APPLICATION = 'auto_apply_queue_new_user_tutorial_application';

const JOB_BOARD_ROUTE_NAME = 'job-board-search';
const QUEUE_ROUTE_NAME = 'job-board-autoapply';
// How the tutorial events name this tutorial, against the existing users' (EN-1250).
const TUTORIAL = 'new_user';
// Every JobBoardAI page, the only place the tutorial takes the user to the queue from.
const JOB_BOARD_ROUTE_PREFIX = 'job-board-';
// How often a jump to the queue checks whether the user is free to go (D5).
const JUMP_POLL_MS = 500;

const PRIMARY_LABELS = { try: 'Try It Now', next: 'Next', review: 'Review', finish: 'Got it' };
// Steps 1 and 2 offer "Exit" rather than "Back": going back from step 2 would not undo its add.
const LAST_EXIT_STEP_INDEX = 1;
// Step 5 waits for the application to be ready; the others are where a paused tutorial picks up (resumeStepIndex).
const QUEUE_ROW_STEP_INDEX = 2;
const COMPLETE_AUTO_APPLY_STEP_INDEX = 4;
const REVIEW_BUTTON_STEP_INDEX = 5;
const PREVIEW_STEP_INDEX = 6;

// A removed application arrives with a null status.
const isFinished = (row) => [null, 'pending', 'sent'].includes(row.status);
// A resume mismatch has no preview to spotlight, so it counts as Couldn't apply (D4).
const hasFailed = (row) => row.status === 'couldnt_apply' || isResumeMismatch(row);
const isReviewable = (row) => REVIEWABLE_STATUSES.includes(row.status) && !isResumeMismatch(row);
// Queue rows can arrive out of order, such as a late preparing row after ready_to_review. `updated_at` counts whole
// seconds, so a preparing row that ties with a row past Preparing is the older one.
const isOlder = (row, than) => {
	const age = Date.parse(row.updated_at) - Date.parse(than.updated_at);

	if (age !== 0) {
		return age < 0;
	}

	return row.status === 'preparing' && than.status !== 'preparing';
};

// The step a paused tutorial picks up at, by its application's status: the row while it is prepared (step 3), its
// Review button once it is ready (step 6), and the preview on its review (step 7). Null on the review of an
// application still prepared.
const resumeStepIndex = (row, isOnReview) => {
	if (isReviewable(row)) {
		return isOnReview ? PREVIEW_STEP_INDEX : REVIEW_BUTTON_STEP_INDEX;
	}

	return isOnReview ? null : QUEUE_ROW_STEP_INDEX;
};

// The preview is taller than the viewport. A desktop has room for the card beside its top. A phone opens the review at
// its top, under the job card, so the card goes above the preview and the page stays put: scrolled to its top, the
// preview would sit under the review's top bar with no room above it (EN-1256).
const previewLayout = () => (isMobileViewport()
	? { scrollBlock: 'none' }
	: { placement: 'left', scrollBlock: 'start' });

/**
 * The eight steps (Figma "New User 1/8" to "8/8"), each tied to the screen it shows on: Job details and the
 * AutoApply tab on the Job Board, the queue, then the review of the application the tutorial follows. Steps 1, 6
 * and 8 let a click through to the button they spotlight. A phone has the same steps on its own layout of each screen
 * (EN-1256); only step 7 places its card differently.
 *
 * @param {string|null} applicationUuid The application added at step 1, unknown until then.
 * @returns {Array<{ screen: 'job-board'|'queue'|'review', target: string, body: string,
 *     primary: 'try'|'next'|'review'|'finish', interactive?: boolean, targetClick?: 'advance',
 *     placement?: 'left', scrollBlock?: 'start'|'none' }>}
 */
const buildSteps = (applicationUuid) => {
	const row = `[data-application-uuid="${applicationUuid}"]`;

	return [
		{
			screen: 'job-board',
			target: '#auto-apply-tutorial-target',
			interactive: true,
			body: 'Try AutoApply. We’ll tailor your resume, cover letter and application answers to this job. All you need '
				+ 'to do is review and submit.',
			primary: 'try',
		},
		{
			screen: 'job-board',
			target: '[data-tutorial-target="autoapply-tab"]',
			body: 'Your application is in the AutoApply queue. We prepare it in the background, so you can keep browsing '
				+ 'jobs.',
			primary: 'next',
		},
		{
			screen: 'queue',
			target: row,
			body: 'Every application you send to AutoApply lives here. Right now we’re tailoring your resume, writing a '
				+ 'cover letter and answering the questions.',
			primary: 'next',
		},
		{
			screen: 'queue',
			target: '[data-tutorial-target="queue-filters"]',
			body: 'When it’s ready, it moves to Ready to review. If the company asks something only you can answer, it '
				+ 'waits in Needs your input.',
			primary: 'next',
		},
		{
			screen: 'queue',
			target: '[data-tutorial-target="complete-auto-apply"]',
			body: 'Want to skip the review step? Turn on Complete Auto Apply and ready applications are sent on their own.',
			primary: 'next',
		},
		{
			screen: 'queue',
			target: `${row} [data-tutorial-target="review"]`,
			interactive: true,
			targetClick: 'advance',
			body: 'Your application is ready. Review it and submit it when everything looks right.',
			primary: 'review',
		},
		{
			screen: 'review',
			target: '[data-tutorial-target="autoapply-preview"]',
			...previewLayout(),
			body: 'We tailored your resume and cover letter to this job and filled in the answers from your profile. Edit '
				+ 'anything that doesn’t feel right.',
			primary: 'next',
		},
		{
			screen: 'review',
			target: '[data-tutorial-target="submit-application"]',
			interactive: true,
			body: 'When you’re ready, submit the application. We’ll send it and you can track it in JobTrackerAI.',
			primary: 'finish',
		},
	];
};

/**
 * The tutorial that takes a queue user who never used AutoApply from the AutoApply button on Job details to their
 * first submitted application (EN-1251). It follows that one application across the Job Board, the queue and its
 * review, so it lives here rather than in a page: JobBoardShell renders it while the user is on its step's screen.
 */
export const useAutoApplyQueueNewUserTutorialStore = defineStore('autoapply-queue-new-user-tutorial', () => {
	const router = useRouter();
	const { getUser } = setAuthState();
	const tour = useTutorialTour();

	// The application the tutorial follows, from the add at step 1, and its latest queue row.
	const applicationUuid = ref(null);
	const trackedRow = ref(null);
	// That application ended in Couldn't apply: the tutorial waits for the next one to be ready for review.
	const isWaiting = ref(false);
	let jumpTimer = null;
	// Where "Back" to step 2 returns: the Job Board as the user left it, with the job they had open.
	let jobBoardLocation = { name: JOB_BOARD_ROUTE_NAME };
	let stopFollowingRoutes = null;
	// Set once step 1 has opened on this page load, which offers it at most once.
	let isOffered = false;
	let isPassed = false;
	// The step this run last reported as shown. The tour reports a step again whenever its card re-enters, such as
	// after exit intent hides it, and a run is each time the tutorial starts or resumes.
	let lastShownStep = null;

	// The cached user is read at every decision, but what this page load saved wins: the credits refresh after an add
	// can replace the cached user with one read before the save.
	const hasPassed = () => isPassed || !!getUser()?.options?.[NEW_USER_TUTORIAL_PASSED];
	const trackedApplicationUuid = () => applicationUuid.value
		?? getUser()?.options?.[NEW_USER_TUTORIAL_APPLICATION]
		?? null;
	const canFollow = () => !!getUser() && isAutoApplyQueueEnabled() && !hasPassed();

	// "Try It Now", or the real AutoApply button, started an add that has not ended. Step 1 hides meanwhile, so it
	// does not cover the resume picker or the paywall.
	const isAddPending = computed(() => useAutoApplyQueueStore().addingJobId !== null
		|| useAutoApplyJobsStore().isSelectingResume);

	const isOnScreen = (screen, { name, query } = router.currentRoute.value) => {
		if (screen === 'job-board') {
			return name === JOB_BOARD_ROUTE_NAME;
		}

		if (name !== QUEUE_ROUTE_NAME) {
			return false;
		}

		// A review, View Details and Apply Externally take the place of the queue's list.
		return screen === 'review'
			? query.review === applicationUuid.value
			: !query.review && !query.details && !query.apply_externally;
	};

	const isVisible = computed(() => tour.active.value
		&& isOnScreen(tour.currentStep.value.screen)
		&& !(tour.isFirstStep.value && isAddPending.value)
		// Exit intent has priority. The step shows again if the user stays.
		&& !useOnboardingRetentionStore().showExitIntentModal);

	// Steps 6 to 8 need the application ready, so step 5 waits for it.
	const isPrimaryDisabled = computed(() => tour.active.value
		&& tour.stepIndex.value === COMPLETE_AUTO_APPLY_STEP_INDEX
		&& trackedRow.value?.status === 'preparing');
	const primaryLabel = computed(() => (isPrimaryDisabled.value
		? 'Preparing…'
		: PRIMARY_LABELS[tour.currentStep.value?.primary]));
	const previousLabel = computed(() => (tour.stepIndex.value <= LAST_EXIT_STEP_INDEX ? 'Exit' : 'Back'));
	const isHighlightingTab = computed(() => isVisible.value && tour.stepIndex.value === 1);

	const screenLocation = (screen) => {
		if (screen === 'job-board') {
			return jobBoardLocation;
		}

		return screen === 'review'
			? { name: QUEUE_ROUTE_NAME, query: { review: applicationUuid.value } }
			: { name: QUEUE_ROUTE_NAME };
	};

	const stopJump = () => {
		clearInterval(jumpTimer);
		jumpTimer = null;
	};

	// Leaving: the step closes and nothing is recorded. From step 2 on, the tutorial resumes on the queue (D4); step 1
	// has no application to resume with, and a later page load offers it again.
	const pause = () => tour.exit();

	// The tour has moved to a step: open its screen, unless the user is on it already. A navigation that does not land
	// there, as a guard refused it or sent it elsewhere, pauses the tutorial like leaving.
	const showStepScreen = async () => {
		const { screen } = tour.currentStep.value;

		if (isOnScreen(screen)) {
			return;
		}

		await router.push(screenLocation(screen));

		if (tour.active.value && !isOnScreen(tour.currentStep.value.screen)) {
			pause();
		}
	};

	const followRoutes = () => {
		stopFollowingRoutes = router.afterEach((to, from, failure) => {
			// A refused navigation, such as one exit intent holds, leaves the user where they are.
			if (failure) {
				return;
			}

			// The Job Board keeps the open job in its address, and moves it to the neighbour once the job is added.
			if (to.name === JOB_BOARD_ROUTE_NAME) {
				jobBoardLocation = to.fullPath;
			}

			// Browser Back, a link, or a guard that sent the tutorial's own navigation elsewhere. Step 1 stays for an
			// add it started, which decides where the tutorial goes (onAdded).
			if (!isOnScreen(tour.currentStep.value.screen, to) && !(tour.isFirstStep.value && isAddPending.value)) {
				pause();
			}
		});
	};

	const onClose = () => {
		stopFollowingRoutes?.();
		stopFollowingRoutes = null;
	};

	// An add that ends without an application (a failure, the paywall, a closed resume picker) closes step 1 without
	// recording it, so a later page load offers it again. An add that succeeded has opened step 2 by then.
	watch(isAddPending, (isPending) => {
		if (!isPending && tour.active.value && tour.isFirstStep.value) {
			tour.exit();
		}
	});

	/**
	 * The tour has shown the card of the step at `stepIndex`: report it once, unless it is the step last reported.
	 *
	 * @param {number} stepIndex
	 */
	const stepShown = (stepIndex) => {
		if (stepIndex === lastShownStep) {
			return;
		}

		lastShownStep = stepIndex;
		trackQueueEvent(AUTOAPPLY_QUEUE_EVENTS.TUTORIAL_STEP_SHOWN, { tutorial: TUTORIAL, step: stepIndex + 1 });
	};

	// The user chose to close the tutorial at the step it is on: not when it ends for another reason, as it does once
	// the application is Pending.
	const trackClosed = (action) => trackQueueEvent(AUTOAPPLY_QUEUE_EVENTS.TUTORIAL_CLOSED, {
		tutorial: TUTORIAL,
		step: tour.stepIndex.value + 1,
		action,
	});

	// "Exit", "Got it" and Escape: the tutorial is done with, and the user stays where they are. So is it once its
	// application is Pending, Sent or removed, as it is after a real Submit click at step 8.
	const end = () => {
		stopJump();
		isWaiting.value = false;
		tour.exit();
		isPassed = true;
		persistTutorialOption(NEW_USER_TUTORIAL_PASSED, true);
	};

	// Opens the tutorial on its application at a later step.
	const open = (startIndex) => {
		stopJump();
		followRoutes();
		lastShownStep = null;
		tour.start(buildSteps(applicationUuid.value), { onClose, startIndex });
	};

	// Couldn't apply: the open step closes where the user is, and the tutorial waits for the next application.
	const waitForNext = () => {
		stopJump();
		isWaiting.value = true;
		tour.exit();
	};

	// The next application ready for review takes the place of the one that failed.
	const retarget = (row) => {
		isWaiting.value = false;
		applicationUuid.value = row.uuid;
		trackedRow.value = row;
		persistTutorialOption(NEW_USER_TUTORIAL_APPLICATION, row.uuid);
	};

	// The application's row in the queue list, or the row an event brought when that one is newer: the tutorial hears
	// of an event before the list does.
	const latestRow = (uuid) => {
		const listed = useAutoApplyQueueStore().applications.find((row) => row.uuid === uuid) ?? null;
		const heard = trackedRow.value?.uuid === uuid ? trackedRow.value : null;

		if (!listed || !heard) {
			return listed ?? heard;
		}

		return isOlder(listed, heard) ? heard : listed;
	};

	/**
	 * Picks the tutorial up on the queue list, or on its application's review, at the step that matches the
	 * application's status. A Pending, Sent or removed application ends it instead. After Couldn't apply it waits, and
	 * continues at step 6 on the first application the queue has ready for review.
	 */
	const resume = () => {
		const queueStore = useAutoApplyQueueStore();
		const uuid = trackedApplicationUuid();

		if (tour.active.value || !uuid || !queueStore.isQueueLoaded || !canFollow()) {
			return;
		}

		// The review screen is told apart by this uuid.
		applicationUuid.value = uuid;
		const isOnReview = isOnScreen('review');

		if (!isOnReview && !isOnScreen('queue')) {
			return;
		}

		if (!isWaiting.value) {
			const row = latestRow(uuid);

			if (!row || isFinished(row)) {
				end();

				return;
			}

			trackedRow.value = row;
			isWaiting.value = hasFailed(row);
		}

		if (isWaiting.value) {
			const next = reviewOrder(queueStore.applications).find((row) => !isResumeMismatch(row));

			if (isOnReview || !next) {
				return;
			}

			retarget(next);
		}

		const startIndex = resumeStepIndex(trackedRow.value, isOnReview);

		if (startIndex !== null) {
			open(startIndex);
		}
	};

	/**
	 * Takes the user to the queue for step 6 once they are free to go (D5): no popup or bottom sheet is open, and no
	 * review, View Details or Apply Externally takes the place of the queue's list. Only on JobBoardAI: once the user
	 * is anywhere else it gives up, and the tutorial resumes on their next queue visit.
	 */
	const jumpWhenClear = () => {
		stopJump();

		const tryJump = () => {
			const { name } = router.currentRoute.value;

			if (!String(name).startsWith(JOB_BOARD_ROUTE_PREFIX)) {
				stopJump();

				return;
			}

			if (document.querySelector('[role="dialog"], [role="alertdialog"]')
				|| (name === QUEUE_ROUTE_NAME && !isOnScreen('queue'))) {
				return;
			}

			stopJump();

			if (isOnScreen('queue')) {
				resume();
			} else {
				router.push({ name: QUEUE_ROUTE_NAME });
			}
		};

		jumpTimer = setInterval(tryJump, JUMP_POLL_MS);
		tryJump();
	};

	// The queue list, or a review, is on screen with the queue loaded.
	watch(() => [router.currentRoute.value.fullPath, useAutoApplyQueueStore().isQueueLoaded], resume, {
		immediate: true,
	});

	/**
	 * Offers step 1 on Job details of an AutoApply job, at most once per page load: to a queue user who never used
	 * AutoApply, has not ended the tutorial and has not added an application from it.
	 */
	const startOnJobDetails = () => {
		if (isOffered || !canFollow() || getUser().has_used_auto_apply || trackedApplicationUuid()) {
			return;
		}

		isOffered = true;
		jobBoardLocation = router.currentRoute.value.fullPath;
		followRoutes();
		lastShownStep = null;
		tour.start(buildSteps(null), { onClose });
	};

	/**
	 * Called once an application has been added to the queue and its card has left the list (EN-1236). The add that
	 * step 1 asked for opens step 2, and the tutorial follows that application from then on. Recorded at once, so a
	 * reload resumes the tutorial rather than offer step 1 again. A user who left the Job Board while the add was
	 * pending finds it at the step their queue screen matches, or paused until their next queue visit.
	 *
	 * @param {{ uuid: string, status: string, updated_at: string }} application The application just added.
	 * @returns {boolean} Whether the tutorial took the add, as step 1 was open: the add is then a new user's.
	 */
	const onAdded = (application) => {
		if (!tour.active.value || !tour.isFirstStep.value) {
			return false;
		}

		applicationUuid.value = application.uuid;
		trackedRow.value = application;
		persistTutorialOption(NEW_USER_TUTORIAL_APPLICATION, application.uuid);
		tour.steps.value = buildSteps(application.uuid);
		tour.next();

		// No navigation or queue load is left to resume it on the queue list the user already has open.
		if (!isOnScreen(tour.currentStep.value.screen)) {
			pause();
			resume();
		}

		return true;
	};

	/**
	 * The main button. "Try It Now" runs AutoApply on the job, like its AutoApply button, and step 1 hides until the add
	 * ends. "Next" and "Review" move to the next step, then open its screen; "Preparing…" does nothing. "Got it" ends
	 * the tutorial.
	 */
	const primary = () => {
		if (!tour.active.value || isPrimaryDisabled.value) {
			return;
		}

		const { primary: action } = tour.currentStep.value;

		if (action === 'try') {
			useAutoApplyJobsStore().startAutoApplyFlow();
		} else if (action === 'finish') {
			trackClosed('got_it');
			end();
		} else {
			tour.next();
			showStepScreen();
		}
	};

	/**
	 * "Exit" on steps 1 and 2 ends the tutorial where the user is: nothing is added at step 1, and the application
	 * added stays queued at step 2. "Back" moves to the previous step, then opens its screen.
	 */
	const previous = () => {
		if (!tour.active.value) {
			return;
		}

		if (tour.stepIndex.value <= LAST_EXIT_STEP_INDEX) {
			trackClosed('exit');
			end();

			return;
		}

		tour.previous();
		showStepScreen();
	};

	/**
	 * Escape, at any step: ends the tutorial where the user is.
	 */
	const escape = () => {
		if (tour.active.value) {
			trackClosed('escape');
			end();
		}
	};

	/**
	 * A click that went through the cut-out to the page, which acts on it. Review opens the review, so step 6 moves on
	 * without opening it again. Submit at step 8 sends the application, and the step stays until that application is
	 * Pending, which ends the tutorial (onApplicationChanged): a refused submit leaves the step where it was. AutoApply
	 * at step 1 starts an add, which hides the step on its own.
	 */
	const onTargetClick = () => {
		if (tour.active.value && tour.currentStep.value.targetClick === 'advance') {
			tour.next();
		}
	};

	/**
	 * Called with every queue row the queue store receives, and with the application's row in the queue list whenever
	 * that changes. The tutorial follows its application's status: step 5's "Preparing…" turns into "Next" once it is
	 * ready, Couldn't apply closes the open step and waits, and Pending, Sent or a removal end the tutorial. While it
	 * waits, the first application ready for review takes the failed one's place (D2), and the user is taken to the
	 * queue for step 6.
	 *
	 * @param {{ uuid: string, status: string|null, updated_at: string }} row
	 */
	const onApplicationChanged = (row) => {
		const uuid = trackedApplicationUuid();

		if (!uuid || !canFollow()) {
			return;
		}

		if (row.uuid === uuid) {
			// A removal is never late.
			if (row.status !== null && trackedRow.value?.uuid === uuid && isOlder(row, trackedRow.value)) {
				return;
			}

			if (isFinished(row)) {
				end();

				return;
			}

			trackedRow.value = row;
		}

		if (isWaiting.value) {
			if (isReviewable(row)) {
				retarget(row);
				jumpWhenClear();
			}
		} else if (row.uuid === uuid && hasFailed(row)) {
			waitForNext();
		}
	};

	// A queue load, such as the one that catches up after a Pusher reconnect, can bring a status no event did. A row the
	// tutorial has heard of already changes nothing, and an older one is dropped.
	watch(
		() => useAutoApplyQueueStore().applications.find((row) => row.uuid === trackedApplicationUuid()) ?? null,
		(row) => {
			if (row) {
				onApplicationChanged(row);
			}
		},
	);

	/**
	 * The step's target never mounted, or left the page: the step closes as if the user had left, so the tutorial
	 * resumes on the next queue visit rather than dead-end.
	 */
	const onTargetMissing = () => pause();

	return {
		active: tour.active,
		stepIndex: tour.stepIndex,
		currentStep: tour.currentStep,
		totalSteps: tour.totalSteps,
		isWaiting,
		isVisible,
		primaryLabel,
		isPrimaryDisabled,
		previousLabel,
		isHighlightingTab,

		startOnJobDetails,
		onAdded,
		primary,
		previous,
		escape,
		stepShown,
		onTargetClick,
		onApplicationChanged,
		onTargetMissing,
	};
});
